import Modal from '../modal/modal';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import ErrorMessage from '../error-message/error-message';
import EmployEngageLogo from '../employ-engage-logo/employ-engage-logo';
import { useEffect, useState } from 'react';
import ResetPasswordForm from '../reset-password-form/reset-password-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useAuthUser } from '../contexts';
import { Button } from '../button/button';
import EmailVerification from 'supertokens-web-js/recipe/emailverification';
import { GET_ALL_COMPANIES } from '../..';
import { useQuery } from '@apollo/client/react/hooks/useQuery';
import kebabCase from 'lodash.kebabcase';
import { describeEnvironment } from '@employ-engage/data';

// Which deployment this build signs in to. Production and development keep
// separate accounts, so the wrong one reports "incorrect email or password"
// for an account that simply lives elsewhere.
const environment = describeEnvironment(import.meta.env.VITE_API_ENDPOINT);

/**
 * A warning, not a status line — so production shows nothing at all.
 *
 * Naming the environment only earns its place where being on the wrong one is
 * a live possibility. On production it is the expected case, so the label was
 * saying "everything is normal" to every customer who ever signed in, and
 * putting an internal hostname on a public screen to say it.
 */
const EnvironmentLabel = () => {
  if (environment.isProduction) return null;

  return (
    <p className="mt-2 text-center text-xs font-semibold text-amber-700">
      <span aria-hidden="true">⚠ </span>
      {environment.name}
      {environment.host ? ` · ${environment.host}` : ''}
    </p>
  );
};

export interface SigninModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  signupMode: 'signup' | 'signin';
  setSignupMode: (signupMode: 'signup' | 'signin') => void;
}

export function SigninModal({
  open,
  setOpen,
  signupMode,
  setSignupMode,
}: SigninModalProps) {
  const { signin, signup } = useAuthUser();
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [
    showResendVerificationEmailPrompt,
    setShowResendVerificationEmailPrompt,
  ] = useState(false);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [verificationEmailSendStatus, setVerificationEmailSendStatus] =
    useState<
      'not_sent' | 'sending' | 'sent' | 'previously_verified' | 'error_sending'
    >('not_sent');
  const handleResendVerificationEmail = async () => {
    try {
      setVerificationEmailSendStatus('sending');
      const response = await EmailVerification.sendVerificationEmail();
      if (response.status === 'EMAIL_ALREADY_VERIFIED_ERROR') {
        setVerificationEmailSendStatus('previously_verified');
      } else if (response.status === 'OK') {
        setVerificationEmailSendStatus('sent');
      }
    } catch (err) {
      // Handle error
      setVerificationEmailSendStatus('error_sending');
    }
  };
  useQuery(GET_ALL_COMPANIES, {
    fetchPolicy: 'cache-and-network',
    skip: signupMode !== 'signup',
  });

  const {
    handleSubmit,
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    resetForm,
  } = useFormik({
    initialValues: {
      email: '',
      companyName: '',
      password: '',
      passwordConfirmation: '',
      general: '',
    },
    onSubmit: async (values, { resetForm, setErrors }) => {
      setLoading(true);
      if (signupMode === 'signup') {
        await signup(
          values.email,
          values.password,
          values.companyName,
          kebabCase(values.companyName)
        )
          .then(() => {
            resetForm();
            setOpen(false);
            setLoading(false);
          })
          .catch((error) => {
            const errorMessage = error.message;
            if (
              errorMessage.includes(
                'E11000 duplicate key error collection: test.preusercompanies index: _id_ dup key'
              )
            ) {
              setErrors({
                companyName: t(
                  'A company with this name already exists. Please choose a different name.'
                ),
              });
            } else {
              setErrors({ general: errorMessage });
            }
            setLoading(false);
          });
      } else {
        signin(values.email, values.password)
          .then(() => {
            resetForm();
            setOpen(false);
            setLoading(false);
          })
          .catch((error) => {
            let errorMessage = error.message;

            if (errorMessage === 'INVALID_CLAIMS') {
              errorMessage = t(
                'Your email is not verified. Please check your inbox.'
              );
              setShowResendVerificationEmailPrompt(true);
            }

            setErrors({ general: errorMessage });
            setLoading(false);
          });
      }
    },
    validationSchema: Yup.object({
      email: Yup.string().required('Required'),
      companyName: Yup.string().when([], {
        is: () => signupMode === 'signup',
        then: (schema) => schema.required('Required'),
        otherwise: (schema) => schema.notRequired(),
      }),
      password: Yup.string().required('Required'),
      passwordConfirmation: Yup.string().when([], {
        is: () => signupMode === 'signup',
        then: (schema) =>
          schema
            .oneOf([Yup.ref('password')], 'Passwords must match')
            .required('Required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
  });

  useEffect(() => {
    resetForm();
  }, [open]);

  return (
    <Modal open={open} setOpen={setOpen} panelClassName="max-w-2xl sm:max-w-lg">
      {showForgotPassword ? (
        <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-sm">
            <div className="flex justify-center">
              <EmployEngageLogo />
            </div>
            <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
              {t('Forgot Password?')}
            </h2>
            <EnvironmentLabel />
          </div>

          <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
            <ResetPasswordForm
              setShowForgotPassword={setShowForgotPassword}
              email={values.email}
            />
          </div>
        </div>
      ) : (
        <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-sm">
            <div className="flex justify-center">
              <EmployEngageLogo />
            </div>
            <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
              {t('Sign in to your account')}
            </h2>
            <EnvironmentLabel />
          </div>

          <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  {t('Email address')}
                </label>
                <div className="mt-2">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={values.email}
                    autoComplete="email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                  <ErrorMessage errors={errors.email} touched={touched.email} />
                </div>
              </div>
              {signupMode === 'signup' && (
                <div>
                  <label
                    htmlFor="companyName"
                    className="block text-sm font-medium leading-6 text-gray-900"
                  >
                    {t('Company Name')}
                  </label>
                  <div className="mt-2">
                    <input
                      id="companyName"
                      name="companyName"
                      type="text"
                      required
                      autoComplete="companyName"
                      onChange={handleChange}
                      value={values.companyName}
                      className="block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                    {errors.companyName && touched.companyName && (
                      <ErrorMessage
                        errors={errors.companyName}
                        touched={touched.companyName}
                      />
                    )}
                  </div>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium leading-6 text-gray-900"
                  >
                    {t('Password')}
                  </label>
                  <div className="text-sm">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="font-semibold text-indigo-600 hover:text-indigo-500"
                    >
                      {t('Forgot password?')}
                    </button>
                  </div>
                </div>
                <div className="mt-2">
                  <div className="flex">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={values.password}
                      autoComplete="current-password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required
                      className="block w-5/6 rounded-l-lg border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setShowPassword((prevState) => !prevState);
                      }}
                      className="w-1/6 rounded-r-lg border-0 p-1.5  shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6 text-center cursor-pointer bg-gray-100 hover:bg-gray-200"
                    >
                      {showPassword ? (
                        <FontAwesomeIcon icon={faEyeSlash} />
                      ) : (
                        <FontAwesomeIcon icon={faEye} />
                      )}
                    </button>
                  </div>
                  {errors.password && touched.password && (
                    <ErrorMessage
                      errors={errors.password}
                      touched={touched.password}
                    />
                  )}
                </div>
              </div>
              {signupMode === 'signup' ? (
                <div>
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="passwordConfirmation"
                      className="block text-sm font-medium leading-6 text-gray-900"
                    >
                      {t('Password Confirmation')}
                    </label>
                  </div>
                  <div className="mt-2">
                    <div className="flex">
                      <input
                        id="passwordConfirmation"
                        name="passwordConfirmation"
                        type={showPassword ? 'text' : 'password'}
                        value={values.passwordConfirmation}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                        className="block w-5/6 rounded-l-lg border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setShowPassword((prevState) => !prevState);
                        }}
                        className="w-1/6 rounded-r-lg border-0 p-1.5  shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6 text-center cursor-pointer bg-gray-100 hover:bg-gray-200"
                      >
                        {showPassword ? (
                          <FontAwesomeIcon icon={faEyeSlash} />
                        ) : (
                          <FontAwesomeIcon icon={faEye} />
                        )}
                      </button>
                    </div>
                    {errors.passwordConfirmation &&
                      touched.passwordConfirmation && (
                        <ErrorMessage
                          errors={errors.passwordConfirmation}
                          touched={touched.passwordConfirmation}
                        />
                      )}
                  </div>
                </div>
              ) : null}
              <ErrorMessage errors={errors.general} touched={touched.general} />
              {showResendVerificationEmailPrompt ? (
                verificationEmailSendStatus === 'not_sent' ? (
                  <div className="text-sm text-gray-500">
                    {t(
                      'Did not receive a verification email? Please check your spam folder or'
                    )}{' '}
                    <Button
                      type="button"
                      variant="link"
                      onClick={handleResendVerificationEmail}
                    >
                      {t('Resend verification email')}
                    </Button>
                  </div>
                ) : verificationEmailSendStatus === 'sending' ? (
                  <div className="text-sm text-gray-500">
                    {t('Sending verification email...')}
                  </div>
                ) : verificationEmailSendStatus === 'sent' ? (
                  <div className="text-sm text-green-500">
                    {t(
                      'Verification email sent. Please check your inbox and spam folder.'
                    )}
                  </div>
                ) : verificationEmailSendStatus === 'previously_verified' ? (
                  <div className="text-sm text-green-500">
                    {t('This email is already verified. Please sign in.')}
                  </div>
                ) : verificationEmailSendStatus === 'error_sending' ? (
                  <div className="text-sm text-red-500">
                    {t(
                      'Error sending verification email. Please try again later.'
                    )}
                  </div>
                ) : null
              ) : null}
              <div>
                <Button
                  disabled={loading}
                  type="submit"
                  className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                >
                  {signupMode === 'signin'
                    ? loading
                      ? t('Signing in...')
                      : t('Sign in')
                    : loading
                    ? t('Signing up...')
                    : t('Sign up')}
                </Button>
              </div>
            </form>
            {signupMode === 'signin' ? (
              <p className="mt-10 text-center text-sm/6 text-gray-500">
                {t('Not a member?') + ' '}
                <Button
                  type="button"
                  variant="link"
                  onClick={() => setSignupMode('signup')}
                >
                  {t('Create an account')}
                </Button>
              </p>
            ) : (
              <p className="mt-10 text-center text-sm/6 text-gray-500">
                Already a member?{' '}
                <Button
                  type="button"
                  variant="link"
                  onClick={() => setSignupMode('signin')}
                >
                  {t('Sign in')}
                </Button>
              </p>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

export default SigninModal;
